import { ExpandLess, ExpandMore } from '@mui/icons-material';
import Delete from '@mui/icons-material/Delete';
import {
  Button,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import BilledEntitiesDialog from '@sophocles/views/billing/components/BilledEntitiesDialog';
import {
  getBilledEntitiesCount,
  summarizeBilledEntityKinds,
} from '@sophocles/views/billing/components/formatBilledEntity';
import ValueDisplay from '@sophocles/views/billing/components/ValueDisplay';

const formatMoney = value => `$${Number(value ?? 0).toFixed(2)}`;

const InvoiceDetailTable = ({
  invoiceData,
  setInvoiceData,
  editable = false,
  categorySubtotals = [],
  serverTotal = null,
  invoiceId = null,
}) => {
  const { t } = useTranslation();
  const [editingCell, setEditingCell] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  // The line whose billed entities are open, or null. Holding the line rather
  // than a boolean is what lets the dialog title name it.
  const [billedEntitiesLine, setBilledEntitiesLine] = useState(null);

  // Read-only mode renders the server's authoritative per-category subtotals
  // (keyed by category name) and grand total. The null key backs the
  // uncategorized bucket; a named group always resolves to its own entry.
  const subtotalsByName = new Map((categorySubtotals ?? []).map(entry => [entry.name, entry]));
  const serverGroupFor = name => subtotalsByName.get(name) ?? subtotalsByName.get(null);
  const serverTotalQuantity = (categorySubtotals ?? []).reduce((acc, entry) => acc + parseFloat(entry.quantity), 0);

  // Edit-preview aggregates: while the invoice is edited, the subtotal/total
  // rows reflect the in-memory working copy. Read-only mode uses the server
  // figures above instead, since category_subtotals/total describe only
  // persisted state and would ignore unsaved line edits.
  const computeSubtotal = items => {
    return items.reduce((acc, item) => acc + item.quantity * item.price, 0);
  };

  const computeQuantity = items => items.reduce((acc, item) => acc + item.quantity, 0);

  const computeTotalQuantity = () => {
    return invoiceData.reduce((acc, category) => acc + computeQuantity(category.items), 0);
  };

  const computeTotal = () => {
    return invoiceData.reduce((acc, category) => acc + computeSubtotal(category.items), 0);
  };

  const handleCellClick = (categoryIndex, itemIndex, field) => {
    setEditingCell({ categoryIndex, itemIndex, field });
  };

  const handleCellChange = (e, categoryIndex, itemIndex, field) => {
    const newInvoiceData = [...invoiceData];
    let floatValue = parseFloat(e.target.value);
    if (isNaN(floatValue)) {
      floatValue = 0.0;
    }
    newInvoiceData[categoryIndex].items[itemIndex][field] = field === 'name' ? e.target.value : floatValue;
    setInvoiceData(newInvoiceData);
  };

  const handleAddNewItem = categoryIndex => {
    const newItem = { name: '', quantity: 1, price: 0 };
    const newInvoiceData = [...invoiceData];
    newInvoiceData[categoryIndex].items.push(newItem);
    setInvoiceData(newInvoiceData);
    // Set the newly added item's name cell to be the editing cell
    const newItemIndex = newInvoiceData[categoryIndex].items.length - 1; // index of the last item
    setEditingCell({
      categoryIndex,
      itemIndex: newItemIndex,
      field: 'name',
    });
  };

  const handleDeleteItem = (categoryIndex, itemIndex) => {
    const newInvoiceData = [...invoiceData];
    newInvoiceData[categoryIndex].items.splice(itemIndex, 1);
    setInvoiceData(newInvoiceData);
    if (editingCell && editingCell.categoryIndex === categoryIndex && editingCell.itemIndex === itemIndex) {
      setEditingCell(null); // Remove focus if deleted item was being edited
    }
  };

  const toggleExpand = (categoryIndex, itemIndex) => {
    setExpandedRows(prev => ({
      ...prev,
      [`${categoryIndex}-${itemIndex}`]: !prev[`${categoryIndex}-${itemIndex}`],
    }));
  };

  return (
    <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell style={{ width: '55%' }}>{t('billing.name', 'Name')}</TableCell>
            <TableCell align="right" style={{ width: '15%' }}>
              {t('billing.quantity', 'Quantity')}
            </TableCell>
            <TableCell align="right" style={{ width: '15%' }}>
              {t('billing.price', 'Price')}
            </TableCell>
            <TableCell align="right" style={{ width: '15%' }}>
              {t('billing.total', 'Total')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoiceData.map((category, categoryIndex) => (
            <React.Fragment key={categoryIndex}>
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="h6">
                    {category?.name != null ? category?.name : t('billing.uncategorized', 'Uncategorized')}
                  </Typography>
                </TableCell>
              </TableRow>
              {category.items.map((item, itemIndex) => {
                const isExpanded = expandedRows[`${categoryIndex}-${itemIndex}`] || false;
                const billedEntitiesCount = getBilledEntitiesCount(item);
                // "300 radios, 2 groups" — the kinds behind the count, named
                // through the same vocabulary the rest of the app uses.
                const breakdownLabel = summarizeBilledEntityKinds(item)
                  .map(entry => `${entry.count} ${t(entry.labelKey)}`)
                  .join(', ');
                // Browsing them needs the invoice: the grid lists within one.
                const canBrowseBilledEntities = invoiceId != null && item.id != null;

                return (
                  <TableRow key={itemIndex} sx={{ pl: 4 }}>
                    {['name', 'quantity', 'price'].map(field => (
                      <TableCell
                        key={`${categoryIndex}-${itemIndex}-${field}`}
                        align={field === 'name' ? 'left' : 'right'}
                        style={{ borderRight: '1px solid var(--border-default)' }}
                        onClick={editable ? () => handleCellClick(categoryIndex, itemIndex, field) : null}
                      >
                        {editingCell?.categoryIndex === categoryIndex &&
                        editingCell?.itemIndex === itemIndex &&
                        editingCell?.field === field &&
                        editable ? (
                          <TextField
                            fullWidth
                            size="small"
                            hiddenLabel
                            value={item[field]}
                            onChange={e => handleCellChange(e, categoryIndex, itemIndex, field)}
                            onBlur={() => setEditingCell(null)}
                            autoFocus
                          />
                        ) : field === 'price' ? (
                          `$${item[field].toFixed(2)}`
                        ) : field === 'quantity' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {item[field]} <ValueDisplay value={item.quantity_diff} />
                          </div>
                        ) : field === 'name' ? (
                          <>
                            <Typography variant="body1" style={{ display: 'inline' }}>
                              {item[field]}
                            </Typography>
                            {item.description && (
                              <div>
                                <Typography
                                  variant="caption"
                                  style={{
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: isExpanded ? 'unset' : 3, // Limits to 3 lines if not expanded
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'pre-line',
                                  }}
                                >
                                  {item.description}
                                </Typography>
                                {item.description.split('\n').length > 3 && (
                                  <IconButton size="small" onClick={() => toggleExpand(categoryIndex, itemIndex)}>
                                    {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                                  </IconButton>
                                )}
                              </div>
                            )}
                            {billedEntitiesCount > 0 && (
                              <div>
                                <Tooltip
                                  title={
                                    canBrowseBilledEntities
                                      ? t('billing.viewBilledEntities', 'View billed entities')
                                      : breakdownLabel
                                  }
                                >
                                  <Chip
                                    size="small"
                                    variant="outlined"
                                    clickable={canBrowseBilledEntities}
                                    label={t('billing.devicesCount', '{{count}} devices', {
                                      count: billedEntitiesCount,
                                    })}
                                    onClick={
                                      canBrowseBilledEntities
                                        ? e => {
                                            e.stopPropagation();
                                            setBilledEntitiesLine({ id: item.id, name: item.name });
                                          }
                                        : undefined
                                    }
                                    sx={{ mt: 0.5 }}
                                  />
                                </Tooltip>
                                {breakdownLabel && (
                                  <Typography variant="caption" component="div" sx={{ color: 'var(--text-secondary)' }}>
                                    {breakdownLabel}
                                  </Typography>
                                )}
                              </div>
                            )}
                          </>
                        ) : (
                          item[field]
                        )}
                        {field === 'name' && editable && (
                          <IconButton
                            size="small"
                            onClick={e => {
                              e.stopPropagation(); // Prevents triggering the name cell's onClick
                              handleDeleteItem(categoryIndex, itemIndex);
                            }}
                            style={{ color: 'var(--text-secondary)', float: 'right' }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    ))}
                    <TableCell align="right">${(item.quantity * item.price).toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
              {editable && (
                <TableRow>
                  <TableCell colSpan={4} align="right">
                    <Button variant="outlined" onClick={() => handleAddNewItem(categoryIndex)}>
                      {t('billing.lineItem', '+ Line Item')}
                    </Button>
                  </TableCell>
                </TableRow>
              )}
              <TableRow style={{ borderBottom: '2px solid var(--border-default)' }}>
                <TableCell>{t('billing.subtotal', 'Subtotal')}</TableCell>
                <TableCell align="right">
                  {editable
                    ? computeQuantity(category.items)
                    : parseFloat(serverGroupFor(category.name)?.quantity ?? 0)}
                </TableCell>
                <TableCell />
                <TableCell align="right">
                  {editable
                    ? `$${computeSubtotal(category.items).toFixed(2)}`
                    : formatMoney(serverGroupFor(category.name)?.subtotal)}
                </TableCell>
              </TableRow>
            </React.Fragment>
          ))}
          <TableRow>
            <TableCell>{t('billing.total', 'Total')}</TableCell>
            <TableCell align="right">{editable ? computeTotalQuantity() : serverTotalQuantity}</TableCell>
            <TableCell />
            <TableCell align="right">{editable ? `$${computeTotal().toFixed(2)}` : formatMoney(serverTotal)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
      {billedEntitiesLine && (
        <BilledEntitiesDialog
          open
          onClose={() => setBilledEntitiesLine(null)}
          invoiceId={invoiceId}
          invoiceItemId={billedEntitiesLine.id}
          lineName={billedEntitiesLine.name}
        />
      )}
    </>
  );
};

export default InvoiceDetailTable;
